import React, { ReactElement } from 'react'
import * as rn from 'react-native'

import * as c from '../../../common'
import * as r from '../../../react-utils'

import AccountCircle from '@mui/icons-material/AccountCircle'
import AppBar from '@mui/material/AppBar'
import Box from '@mui/material/Box'
import IconButton from '@mui/material/IconButton'
import LocalOfferIcon from '@mui/icons-material/LocalOffer'
import LogoutIcon from '@mui/icons-material/Logout'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import MoreIcon from '@mui/icons-material/MoreVert'
import SecurityUpdateIcon from '@mui/icons-material/SecurityUpdate'
import Toolbar from '@mui/material/Toolbar'
import Typography from '@mui/material/Typography'
import { Link } from 'react-router-dom'

import * as gs from '../../../app/gStyles'
import Pad from '../Pad'
import { auth } from '../../fire'

//@ts-ignore
import styles from './NavBar.module.css'
import { SxProps } from '@mui/material'

export interface NavBarProps {
  children?: ReactElement
}

export default React.memo<NavBarProps>(function NavBar({ children }) {
  const t = r.useTheme()
  const isDark = rn.useColorScheme() === 'dark'
  const admins = r.useEnv<c.Dict<boolean>>('admins')
  const isAdmin = !!admins?.[auth.currentUser?.uid ?? '']

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null)
  const [mobileMoreAnchorEl, setMobileMoreAnchorEl] =
    React.useState<null | HTMLElement>(null)

  const isMenuOpen = Boolean(anchorEl)
  const isMobileMenuOpen = Boolean(mobileMoreAnchorEl)

  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleMobileMenuClose = () => {
    setMobileMoreAnchorEl(null)
  }

  const handleMenuClose = () => {
    setAnchorEl(null)
    handleMobileMenuClose()
  }

  const handleMobileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setMobileMoreAnchorEl(event.currentTarget)
  }

  const menuIconSx = React.useMemo(
    (): SxProps => ({
      color: t.canvas.color as string,
      fontSize: '32px',
    }),
    [t.canvas.color],
  )

  const menuId = 'primary-search-account-menu'
  const renderMenu = (
    <Menu
      anchorEl={anchorEl}
      anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      classes={{ list: styles['menu'] }}
      id={menuId}
      keepMounted
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      open={isMenuOpen}
      onClose={handleMenuClose}
    >
      <MenuItem className={styles['menu']} onClick={() => auth.signOut()}>
        <LogoutIcon sx={menuIconSx} />

        <Typography
          color={t.canvas.color as string}
          fontFamily={t.canvas.fontFamily}
        >
          Log Out
        </Typography>
      </MenuItem>

      <MenuItem>
        <SecurityUpdateIcon sx={menuIconSx} />

        <a href="itms-services://?action=download-manifest&url=https://www.ecohomereports.com/manifest.plist">
          <Typography fontFamily={t.canvas.fontFamily}>
            Install iOS app
          </Typography>
        </a>
      </MenuItem>

      {isAdmin && (
        <Link className={styles['menu-link']} to="/item-prices">
          <MenuItem onClick={handleMenuClose}>
            <LocalOfferIcon sx={menuIconSx} />

            <Typography
              color={t.canvas.color as string}
              fontFamily={t.canvas.fontFamily}
            >
              Item Prices
            </Typography>
          </MenuItem>
        </Link>
      )}

      <MenuItem>
        <Typography
          color={t.canvas.color as string}
          fontFamily={t.canvas.fontFamily}
        >
          Version: {process.env['REACT_APP_APP_VERSION']} (#
          {process.env['REACT_APP_COMMIT_SHORT_HASH']})
        </Typography>
      </MenuItem>
    </Menu>
  )

  const mobileMenuId = 'primary-search-account-menu-mobile'
  const renderMobileMenu = (
    <Menu
      anchorEl={mobileMoreAnchorEl}
      anchorOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
      classes={{ list: styles['menu'] }}
      id={mobileMenuId}
      keepMounted
      transformOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
      open={isMobileMenuOpen}
      onClose={handleMobileMenuClose}
    >
      <MenuItem>
        <AccountCircle sx={menuIconSx} />
        <Typography color={t.canvas.color as string} fontFamily={t.fontFamily}>
          {auth.currentUser?.email}
        </Typography>
      </MenuItem>

      <MenuItem>
        <SecurityUpdateIcon sx={menuIconSx} />
        <Link to="itms-services://?action=download-manifest&url=https://www.ecohomereports.com/manifest.plist">
          Install iOS app
        </Link>
      </MenuItem>

      {isAdmin && (
        <Link className={styles['menu-link']} to="/item-prices">
          <MenuItem onClick={handleMobileMenuClose}>
            <LocalOfferIcon sx={menuIconSx} />
            <Typography
              color={t.canvas.color as string}
              fontFamily={t.fontFamily}
            >
              Item Prices
            </Typography>
          </MenuItem>
        </Link>
      )}

      <MenuItem onClick={() => auth.signOut()}>
        <LogoutIcon sx={menuIconSx} />
        <Typography color={t.canvas.color as string} fontFamily={t.fontFamily}>
          Log Out
        </Typography>
      </MenuItem>
      <MenuItem>
        <Typography color={t.canvas.color as string} fontFamily={t.fontFamily}>
          Version: {process.env['REACT_APP_APP_VERSION']} (#
          {process.env['REACT_APP_COMMIT_SHORT_HASH']})
        </Typography>
      </MenuItem>
    </Menu>
  )

  return (
    <Box sx={gs.width100}>
      <AppBar position="static">
        <Toolbar>
          <Link to="/customers">
            <Box
              sx={{
                display: {
                  xs: children ? 'none' : 'flex',
                  sm: 'flex',
                },
                marginRight: '40px',
                width: {
                  xs: '80px',
                  sm: '120px',
                },
              }}
            >
              <img
                src={isDark ? c.LOGO_BIG_DARK : c.LOGO_BIG}
                alt="Logotype"
                style={{ width: '100%' }}
              />
            </Box>
          </Link>

          {children && children}
          <Box sx={{ flexGrow: 1 }} />
          <Box sx={{ display: { xs: 'none', md: 'flex' } }}>
            <Typography
              color={t.brand__color as string}
              fontFamily={t.fontFamily}
              sx={{ alignSelf: 'center' }}
            >
              {auth.currentUser?.email}
            </Typography>
            <IconButton
              size="large"
              edge="end"
              aria-label="account of current user"
              aria-controls={menuId}
              aria-haspopup="true"
              onClick={handleProfileMenuOpen}
            >
              <AccountCircle sx={{ color: t.brand__color as string }} />
            </IconButton>
            <Pad amt={30} row />
          </Box>
          <Box sx={{ display: { xs: 'flex', md: 'none' }, padding: 0 }}>
            <IconButton
              size="large"
              aria-label="show more"
              aria-controls={mobileMenuId}
              aria-haspopup="true"
              onClick={handleMobileMenuOpen}
              sx={{ color: t.brand__color as string, padding: 0 }}
            >
              <MoreIcon />
            </IconButton>
          </Box>
        </Toolbar>
      </AppBar>
      {renderMobileMenu}
      {renderMenu}
    </Box>
  )
})

// const SearchIconWrapper = styled('div')(({ theme }) => ({
//   padding: theme.spacing(0, 2),
//   height: '100%',
//   position: 'absolute',
//   pointerEvents: 'none',
//   display: 'flex',
//   alignItems: 'center',
//   justifyContent: 'center',
// }))

// const StyledInputBase = styled(InputBase)(({ theme }) => ({
//   color: 'inherit',
//   '& .MuiInputBase-input': {
//     padding: theme.spacing(1, 1, 1, 0),
//     // vertical padding + font size from searchIcon
//     paddingLeft: `calc(1em + ${theme.spacing(4)})`,
//     transition: theme.transitions.create('width'),
//     width: '100%',
//     [theme.breakpoints.up('md')]: {
//       width: '20ch',
//     },
//   },
// }))
